import { createContext, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useIsFetching, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { FlaskConical,
  Archive,
  Bell,
  Building2,
  CalendarRange,
  Check,
  CreditCard,
  Filter,
  Flame,
  Globe,
  Home,
  Layers,
  LogOut,
  Menu,
  Plug,
  Plus,
  RefreshCw,
  Search,
  Settings,
  Sparkles,
  Target,
  Telescope,
  UserCircle,
  BookOpen,
  Repeat,
} from 'lucide-react';
import {
  ACTIVE_PID,
  getHealth,
  getOrgNotices,
  getOrgProjects,
  getOrgUsage,
  getOrgs,
  logout,
  markOrgNoticesRead,
  qk,
  type OrgNotice,
  type OrgSummary,
  type WebhookEventName,
} from '../api.js';
import {
  ACCOUNT_PATH,
  LOGIN_PATH,
  ONBOARDING_PATH,
  ORG_ROLE_LABELS,
  ROLE_LABELS,
} from '../lib/auth.js';
import { formatDateTime, formatNumber } from '../lib/format.js';
/*
 * §75.4 — kota/abonelik bildirimleri Faturalama'ya götürür. Yol sabiti sayfanın
 * KENDİSİNDEN okunur; burada ikinci bir '/faturalama' dizesi yazmak, sayfa taşındığında
 * sessizce kırılacak bir kopya olurdu.
 */
import { BILLING_PATH } from '../pages/Billing.js';
import { useAuth } from './AuthGate.js';
import BrandMark from './BrandMark.js';
import CommandPalette from './CommandPalette.js';

/**
 * Uygulama kabuğu — referans DXP admin paneliyle birebir:
 *   · 48px sabit beyaz üst çubuk (site seçici + marka + arama + aksiyonlar)
 *   · 88px sabit ikon rayı (ikon üstte, etiket altta; aktif olan #f3f4f6 zeminli)
 *   · #fafafa sayfa zemini, içerik solda 88px boşlukla başlar
 *
 * Gezinme sırası §13.2'yi izler: "Bulgular (ana sayfa yapılacak)" ilk sıradadır ve
 * marka turuncusuyla vurgulanır; '/' rotası §13.1 gereği Genel Bakış olarak KALIR.
 */

/* -------------------------------------------------------------------------- */
/* Aktif proje                                                                 */
/* -------------------------------------------------------------------------- */

export interface ProjectOption {
  id: string;
  name: string;
  /** §72.4 — arşiv damgası; dolu ise seçicide "Arşiv" başlığı altında listelenir. */
  archived_at?: string | null;
  /**
   * §23.5 — ısı haritası arka planının adresi. Bağlamda TAŞINIR ki sayfa ikinci bir
   * proje sorgusu açmasın (§72.5/4: panelde tek proje kaynağı).
   */
  domain?: string | null;
}

interface ProjectContextValue {
  pid: string;
  setPid: (pid: string) => void;
  projects: ProjectOption[];
  /** §42.5 — kullanıcının org'ları (seçici bunu çizer). */
  orgs: OrgSummary[];
  /** Seçili org; org yoksa null (§43.2 sihirbazı bu durumu okur). */
  orgId: string | null;
  setOrgId: (oid: string) => void;
  /** Kullanıcının SEÇİLİ org'daki rolü; org yoksa null. */
  orgRole: OrgSummary['role'] | null;
}

/**
 * Bağlamın KENDİSİ dışa açılır: DOM testleri sayfaları kabuğun tamamını (üst çubuk,
 * ikon rayı, Router) kurmadan gerçek bir proje bağlamıyla render edebilsin. Alternatifi
 * her sayfanın kendi proje sorgusunu açmasıydı — §72.5/4'te kapatılan tam olarak o.
 */
export const ProjectContext = createContext<ProjectContextValue>({
  pid: ACTIVE_PID,
  setPid: () => undefined,
  projects: [],
  orgs: [],
  orgId: null,
  setOrgId: () => undefined,
  orgRole: null,
});

/**
 * §42.5 — seçili org localStorage'da tutulur.
 *
 * NEDEN localStorage: org seçimi bir GÖRÜNÜM tercihidir, yetki değil — sunucu her
 * istekte org rolünü yeniden ölçer (§42.5 preHandler), dolayısıyla burada tutulan
 * değerin kurcalanması yetki KAZANDIRMAZ, yalnız 403'e yol açar.
 *
 * Anahtar KULLANICIYA GÖRE ayrılmaz ama çıkışta SİLİNİR (`clearActiveOrg`): aksi
 * hâlde aynı tarayıcıda B kullanıcısı, A'nın son org'uyla açılır ve ilk sayfada
 * 403 görürdü.
 */
const ORG_KEY = 'dxp_org';

export function readActiveOrg(): string | null {
  try {
    const v = window.localStorage.getItem(ORG_KEY);
    return v === null || v === '' ? null : v;
  } catch {
    return null; // private mode
  }
}

export function clearActiveOrg(): void {
  try {
    window.localStorage.removeItem(ORG_KEY);
  } catch {
    /* private mode */
  }
}

function writeActiveOrg(oid: string): void {
  try {
    window.localStorage.setItem(ORG_KEY, oid);
  } catch {
    /* private mode */
  }
}

/**
 * Seçili proje de localStorage'da tutulur — org ile BİREBİR aynı gerekçe (§42.5).
 *
 * NEDEN: seçim yalnız React state'inde dururken sayfa yenilemesi onu `projects[0]`'a
 * geri düşürüyordu; kullanıcı B projesini seçip F5'e bastığında A projesine dönüyor
 * ve "seçim kaydedilmedi" sanıyordu. Proje seçimi de bir GÖRÜNÜM tercihidir: sunucu
 * her istekte pid üyeliğini yeniden ölçer, dolayısıyla buradaki değerin kurcalanması
 * yetki KAZANDIRMAZ, olsa olsa 403'e yol açar — ve aşağıdaki uyum etkisi, listede
 * olmayan bir pid'i zaten ilk aktif projeye düzeltir.
 *
 * Çıkışta org ile BİRLİKTE silinir: aksi hâlde aynı tarayıcıda B kullanıcısı, A'nın
 * son projesiyle açılır ve ilk sayfada 403 görürdü.
 */
const PID_KEY = 'dxp_pid';

export function readActivePid(): string | null {
  try {
    const v = window.localStorage.getItem(PID_KEY);
    return v === null || v === '' ? null : v;
  } catch {
    return null; // private mode
  }
}

export function clearActivePid(): void {
  try {
    window.localStorage.removeItem(PID_KEY);
  } catch {
    /* private mode */
  }
}

function writeActivePid(pid: string): void {
  try {
    window.localStorage.setItem(PID_KEY, pid);
  } catch {
    /* private mode */
  }
}

/** Sayfalar aktif projeyi buradan okur — pid'i tekrar sabitlemesinler. */
export function useActivePid(): string {
  return useContext(ProjectContext).pid;
}

export function useProjectContext(): ProjectContextValue {
  return useContext(ProjectContext);
}

/** §42.5 — seçili org bağlamı (org sayfaları ve sihirbaz bunu okur). */
export function useOrgContext(): Pick<
  ProjectContextValue,
  'orgs' | 'orgId' | 'setOrgId' | 'orgRole'
> {
  const { orgs, orgId, setOrgId, orgRole } = useContext(ProjectContext);
  return { orgs, orgId, setOrgId, orgRole };
}

/* -------------------------------------------------------------------------- */
/* Gezinme                                                                     */
/* -------------------------------------------------------------------------- */

/**
 * Ray genişliği ÜÇ yerde birden geçer: rayın kendisi, sayfa gövdesinin sol boşluğu
 * ve kota şeridinin sol kenarı. Ayrışırlarsa belirti sessizdir — şerit rayın altına
 * kayar ya da içerik rayın ardında kalır.
 *
 * NEDEN şablon değil de üç ayrı dize: Tailwind sınıf adlarını KAYNAK METİNDEN tarar;
 * `w-[${RAIL}px]` yazılırsa hiçbir kural üretilmez ve ray genişliksiz kalır. Değer
 * elde tutulur ama tek yerde toplanır — üçü BİRLİKTE değişir.
 */
const RAIL_W = 'w-[88px]';
const RAIL_PAD = 'pl-[88px]';
const RAIL_LEFT = 'left-[88px]';

export interface NavItem {
  to: string;
  label: string;
  /** Yalnız tam eşleşmede aktif ('/' rotası için zorunlu). */
  end?: boolean;
  /** §13.2 — "ana sayfa yapılacak": kalıcı marka vurgusu. */
  primary?: boolean;
  icon: typeof Home;
  description: string;
  /**
   * Rayda çizilecek etiket; verilmezse `label` kullanılır.
   *
   * Değer bir KISALTMA DEĞİL, yumuşak tire (U+00AD) taşıyan AYNI kelimedir:
   * tarayıcı yalnızca satır taştığında oradan böler ("Organi-/zasyon"), sığdığı
   * yerde tire GÖRÜNMEZ. Bu yüzden ⌘K paleti ve arama tam `label`'ı kullanmaya
   * devam eder — "organizasyon" araması bölünmüş bir dizeye takılmaz.
   *
   * BUGÜNKÜ 88px'lik rayda (iç genişlik 75px) iki etiket de tek satıra SIĞAR ve
   * tire hiç görünmez; alan yalnız iki etikette DURUYOR çünkü sığma payı incedir:
   * ölçüldü, "Organizasyon" 65.6px, "Webhook'lar" 61.5px (Plus Jakarta Sans
   * 10px/500). Yazı tipi indirilemediğinde yedek (Inter/system-ui) daha geniş
   * çizer ve pay tükenir — o durumda etiket kırpılmak yerine bölünür.
   */
  railLabel?: string;
}

/** §13 sayfa listesi — sıra §13.2 notuna göre Bulgular ile başlar. */
export const NAV_ITEMS: NavItem[] = [
  {
    to: '/bulgular',
    label: 'Bulgular',
    primary: true,
    description: 'Öncelik sıralı içgörüler',
    icon: Sparkles,
  },
  { to: '/', label: 'Genel Bakış', end: true, description: 'Oturum, dönüşüm, gelir', icon: Home },
  { to: '/deneyler', label: 'Deneyler', description: 'A/B deneyleri ve sonuçları', icon: FlaskConical },
  { to: '/funnel', label: 'Funnel', description: 'Otomatik keşfedilen huni', icon: Filter },
  { to: '/hedefler', label: 'Hedefler', description: 'Keşfedilen hedef adayları', icon: Target },
  { to: '/oturumlar', label: 'Oturumlar', description: 'Hata / rage / terk / dönüşüm', icon: Layers },
  // §23.5 — ısı haritası; yol şartnamede İNGİLİZCE ('/heatmap'), etiket Türkçedir.
  {
    to: '/heatmap',
    label: 'Isı Haritası',
    description: 'Tıklama yoğunluğu ve scroll erişimi',
    icon: Flame,
  },
  /*
   * §24.7 — SEO tarayıcı paneli. Etiket kısaltmadır ("SEO"), bu yüzden Türkçeleştirilmez.
   * İkon `Telescope`: bu sayfanın verisi RUM'dan değil, siteyi kendi gezen tarayıcıdan
   * gelir (§24.1) — "kullanıcıyı izlemek" değil "siteyi taramak" işidir.
   */
  {
    to: '/seo',
    label: 'SEO',
    description: 'Tarayıcı bulguları: meta, kırık link, noindex',
    icon: Telescope,
  },
  /*
   * §19.3 — uzun vadeli yönetici raporu. Gündelik sayfaların ARDINDA durur: içeriği
   * gece penceresinde hazırlanmış 12-24 aylık trenddir, canlı bir teşhis ekranı değil.
   * İkon `CalendarRange`: buradaki büyüklük "şu an" değil "dönem"dir.
   */
  {
    to: '/uzun-vadeli',
    label: 'Uzun Vadeli',
    description: '12-24 aylık trend, YoY, kanal karması',
    icon: CalendarRange,
  },
  /*
   * §93 — retention kohortları. Uzun Vadeli'nin YANINDA durur (dönemsel halka):
   * gündelik teşhis değil, dönemsel sağlık göstergesidir. İkon `Repeat`:
   * buradaki soru "geldiler mi" değil "GERİ geldiler mi"dir.
   */
  {
    to: '/retention',
    label: 'Geri Dönüş',
    description: 'Haftalık kohortlar: ziyaretçiler geri geliyor mu',
    icon: Repeat,
  },
  /*
   * §21.3 / §26.6 — Ayarlar. Rayın SONUNDA durur: gündelik iş akışı (bulgu → veri)
   * değil, ara sıra dokunulan bir yapılandırma sayfasıdır.
   */
  {
    to: '/ayarlar',
    label: 'Ayarlar',
    description: 'E-posta raporu aboneleri ve proje üyeleri',
    icon: Settings,
  },
  /*
   * §42.5 / §46.5 — Organizasyon. Ayarlar'dan hemen SONRA gelir: Ayarlar PROJE
   * yapılandırmasıdır, burası ORG'un (üyeler, kurumsal giriş). §46.7 ikisini
   * birbirine bağlar — SSO'ya geçen org önce çalışanlarını ORG ÜYESİ yapmalıdır.
   */
  {
    to: '/organizasyon',
    label: 'Organizasyon',
    railLabel: 'Organi­zasyon',
    description: 'Org üyeleri ve kurumsal giriş (SSO)',
    icon: Building2,
  },
  /*
   * §45 — Faturalama. Ayarlar'ın ARDINDA durur ve bu bilinçlidir: Ayarlar PROJE
   * yapılandırmasıdır, burası ORG'un (plan, abonelik, fatura) — rayın en dışındaki
   * halka, en seyrek dokunulan ve en geniş kapsamlı olandır. §44.4 kota banner'ı
   * "planınızı yükseltmeyi düşünebilirsiniz" dediğinde kullanıcının arayacağı yer.
   */
  {
    to: '/faturalama',
    label: 'Faturalama',
    description: 'Plan, abonelik durumu ve fatura geçmişi',
    icon: CreditCard,
  },
  /*
   * §47 — Webhook'lar. Faturalama'yla aynı halkadadır (ikisi de ORG düzeyi) ve onun
   * ARDINDA durur: fatura her org'u ilgilendirir, webhook yalnız entegrasyon kuran
   * müşteriyi. İkon `Plug`: burada üretilen şey bir rapor değil, başka bir sisteme
   * takılan bir uçtur.
   */
  {
    to: '/webhooks',
    label: 'Webhook’lar',
    railLabel: 'Web­hook’lar',
    description: 'Olay abonelikleri, teslimat geçmişi, yeniden deneme',
    icon: Plug,
  },
  /*
   * Kullanma kılavuzu — rayın EN SONUNDA durur: ürün sayfası değil, ürünün
   * açıklamasıdır. NAV_ITEMS üzerinden ⌘K paletine de otomatik girer; ayrı bir
   * "yardım" girişi kopyalanmaz.
   */
  {
    to: '/kilavuz',
    label: 'Kılavuz',
    description: 'Ekranların ne işe yaradığı, akışlar, sorun giderme',
    icon: BookOpen,
  },
];

/* -------------------------------------------------------------------------- */
/* Üst çubuk                                                                   */
/* -------------------------------------------------------------------------- */

function useOutsideClose(open: boolean, onClose: () => void) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent): void => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open, onClose]);
  return ref;
}

function HeaderProjectPicker() {
  const [open, setOpen] = useState(false);
  const ref = useOutsideClose(open, () => setOpen(false));
  const navigate = useNavigate();
  const { pid, setPid, projects, orgs, orgId, setOrgId, orgRole } = useProjectContext();

  /*
   * §72.4 — arşivliler AYRI bölümde ve AŞAĞIDA durur. Aynı listede soluk bir satır
   * olarak göstermek, seçmek isteyeni "neden bu farklı" sorusuyla baş başa bırakırdı;
   * ayrı başlık durumu SÖYLER.
   */
  const aktifler = projects.filter((p) => (p.archived_at ?? null) === null);
  const arsivliler = projects.filter((p) => (p.archived_at ?? null) !== null);

  /*
   * §72.1 — "+ Yeni proje" YALNIZ org rolü ≥ admin olana çizilir; uçtaki kural da
   * budur (`POST /api/orgs/:oid/projects` → org:admin). Yetkisiz kullanıcıya
   * tıklayınca 403 alacağı bir buton çizmek, boş bir söz vermektir.
   */
  const projeAcabilir = orgRole === 'admin' || orgRole === 'owner';

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label="Proje seç"
        className={`flex h-8 w-8 items-center justify-center rounded-lg transition-colors ${
          open ? 'bg-[#f3f4f6] text-[#111827]' : 'text-[#374151] hover:bg-[#f3f4f6]'
        }`}
      >
        <Menu className="h-5 w-5" />
      </button>

      {open ? (
        <div className="absolute left-0 top-10 z-50 w-56 overflow-hidden rounded-xl border border-[#e5e7eb] bg-white shadow-xl">
          {/*
            §42.5 — ORG seçici, proje seçicisinin ÜSTÜNDE durur ve YALNIZ birden çok
            org varsa çizilir. Tek org'lu kurulumda (backfill sonrası herkes böyledir)
            gösterilecek bir SEÇİM yoktur; boş bir seçici, olmayan bir karar varmış
            gibi görünürdü.
          */}
          {orgs.length > 1 ? (
            <div className="border-b border-[#f3f4f6]" data-testid="org-secici">
              <p className="flex items-center gap-1.5 px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-[#9ca3af]">
                <Building2 className="h-3 w-3" /> Organizasyon
              </p>
              <div className="max-h-40 overflow-y-auto pb-1">
                {orgs.map((org) => {
                  const aktif = org.id === orgId;
                  return (
                    <button
                      key={org.id}
                      type="button"
                      data-testid="org-secenek"
                      data-org={org.id}
                      onClick={() => {
                        setOrgId(org.id);
                        setOpen(false);
                      }}
                      className={`flex w-full items-center justify-between px-3 py-2 text-left transition-colors hover:bg-[#f9fafb] ${
                        aktif ? 'text-brand' : 'text-[#374151]'
                      }`}
                    >
                      <span className="min-w-0">
                        <span className="block truncate text-[13px] font-medium">{org.name}</span>
                        <span className="block truncate text-[11px] text-[#9ca3af]">
                          {ORG_ROLE_LABELS[org.role]}
                        </span>
                      </span>
                      {aktif ? <Check className="ml-2 h-3.5 w-3.5 shrink-0 text-brand" /> : null}
                    </button>
                  );
                })}
              </div>
            </div>
          ) : null}

          <div className="border-b border-[#f3f4f6] px-3 py-2">
            <p className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-[#9ca3af]">
              <Globe className="h-3 w-3" /> Proje Seç
            </p>
          </div>
          <div className="max-h-64 overflow-y-auto">
            {aktifler.map((project) => {
              const active = project.id === pid;
              return (
                <button
                  key={project.id}
                  type="button"
                  data-testid="proje-secenek"
                  data-pid={project.id}
                  onClick={() => {
                    setPid(project.id);
                    setOpen(false);
                  }}
                  className={`flex w-full items-center justify-between px-3 py-2.5 text-left transition-colors hover:bg-[#f9fafb] ${
                    active ? 'text-brand' : 'text-[#374151]'
                  }`}
                >
                  <p className="truncate text-[13px] font-medium">{project.name}</p>
                  {active ? <Check className="ml-2 h-3.5 w-3.5 shrink-0 text-brand" /> : null}
                </button>
              );
            })}

            {/*
              §72.5/1 — proje YOKSA sahte bir "Demo Mağaza" GÖSTERİLMEZ. Olmayan bir
              projeyi varmış gibi göstermek, kullanıcıyı her sayfada 403 alacağı bir
              pid'e kilitliyordu; boş durum gerçeği söyler ve çıkışı gösterir.
            */}
            {aktifler.length === 0 ? (
              <p data-testid="proje-yok" className="px-3 py-2.5 text-[12px] text-[#9ca3af]">
                Bu organizasyonda henüz proje yok.
              </p>
            ) : null}

            {arsivliler.length > 0 ? (
              <div className="border-t border-[#f3f4f6]" data-testid="arsiv-bolumu">
                <p className="flex items-center gap-1.5 px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-[#9ca3af]">
                  <Archive className="h-3 w-3" /> Arşiv
                </p>
                {arsivliler.map((project) => {
                  const active = project.id === pid;
                  return (
                    <button
                      key={project.id}
                      type="button"
                      data-testid="proje-secenek"
                      data-pid={project.id}
                      data-arsivli="1"
                      title="Arşivli proje — veri toplanmıyor, geçmiş okunabilir"
                      onClick={() => {
                        setPid(project.id);
                        setOpen(false);
                      }}
                      className={`flex w-full items-center justify-between px-3 py-2.5 text-left transition-colors hover:bg-[#f9fafb] ${
                        active ? 'text-brand' : 'text-[#9ca3af]'
                      }`}
                    >
                      <p className="truncate text-[13px]">{project.name}</p>
                      {active ? <Check className="ml-2 h-3.5 w-3.5 shrink-0 text-brand" /> : null}
                    </button>
                  );
                })}
              </div>
            ) : null}
          </div>

          {projeAcabilir ? (
            <button
              type="button"
              data-testid="yeni-proje"
              onClick={() => {
                setOpen(false);
                /*
                 * §72.1 — sihirbaz YENİDEN YAZILMAZ, bağlanır: org'u olan kullanıcı
                 * için `/kurulum` zaten 2. adımdan (proje formu) başlar ve bitişte
                 * proje listesini tazeler. İkinci bir "proje aç" formu yazmak, aynı
                 * mutasyonun iki ayrı ekranda ayrışması demekti.
                 */
                navigate(ONBOARDING_PATH);
              }}
              className="flex w-full items-center gap-2 border-t border-[#f3f4f6] px-3 py-2.5 text-left text-[13px] font-medium text-brand transition-colors hover:bg-[#fff4ec]"
            >
              <Plus className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
              Yeni proje
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

/**
 * Üst çubuktaki genel "Yenile" düğmesi — açık olan HER sorguyu tazeler.
 *
 * NEDEN sayfa başına değil de burada: kullanıcının sorusu ("veri güncel mi?") sayfaya
 * özgü değildir ve panelde sayfa başına farklı `staleTime`'lar vardır; her sayfaya ayrı
 * bir düğme koymak hem tekrar hem de tutarsız davranış olurdu.
 *
 * `invalidateQueries()` argümansız çağrılır: TÜM anahtarları bayatlatır, ama YALNIZCA
 * o an monte olan sorgular yeniden çekilir (react-query davranışı) — yani ekranda ne
 * varsa o tazelenir, arka planda 40 istek patlamaz.
 *
 * NE YAPMAZ: sunucuda hiçbir işi TETİKLEMEZ. Oturumlar özelinde önemlidir — bir
 * oturumun `dxp.sessions`'a yazılması worker'ın onu KAPATMASINA bağlıdır (§11.5) ve
 * bunu erken zorlamak `daily_agg` toplamlarını bozar. Devam eden oturumları görmek
 * için "canlı" satırları vardır; bu düğme onları da tazeler.
 */
function RefreshButton() {
  const queryClient = useQueryClient();
  // Aktif istek sayısı: dönüş animasyonu gerçek yüklemeye bağlanır, sahte gecikmeye değil.
  const fetching = useIsFetching();

  return (
    <button
      type="button"
      data-testid="yenile-dugmesi"
      onClick={() => void queryClient.invalidateQueries()}
      aria-label="Verileri yenile"
      title="Verileri yenile"
      className="flex h-8 w-8 items-center justify-center rounded-lg transition-colors hover:bg-[#f3f4f6]"
    >
      <RefreshCw
        aria-hidden="true"
        className={`h-4 w-4 text-[#6b7280] ${fetching > 0 ? 'animate-spin' : ''}`}
      />
    </button>
  );
}

function HealthDot() {
  // Sunucu kapalıyken panel çökmemeli: retry kapalı, hata sessizce "bilinmiyor" olur.
  const { data, isError, isLoading } = useQuery({
    queryKey: qk.health(),
    queryFn: ({ signal }) => getHealth(signal),
    retry: false,
    refetchInterval: 60_000,
    staleTime: 30_000,
  });

  const state = isLoading ? 'loading' : isError ? 'down' : (data?.status ?? 'down');
  const tone =
    state === 'ok'
      ? 'bg-emerald-500'
      : state === 'degraded'
        ? 'bg-amber-500'
        : state === 'loading'
          ? 'bg-[#d1d5db]'
          : 'bg-red-500';
  const label =
    state === 'ok'
      ? 'Servisler çalışıyor'
      : state === 'degraded'
        ? 'Kısmi kesinti'
        : state === 'loading'
          ? 'Kontrol ediliyor…'
          : 'API erişilemiyor';

  return (
    <div className="flex items-center gap-1.5" title={label}>
      <span aria-hidden="true" className={`h-1.5 w-1.5 rounded-full ${tone}`} />
      <span className="hidden text-[12px] text-[#9ca3af] lg:inline">{label}</span>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* §75.4 Bildirim zili                                                         */
/* -------------------------------------------------------------------------- */

/**
 * §75.4 — bildirim türünün Türkçe başlığı ve gideceği sayfa.
 *
 * Katalog `WEBHOOK_EVENT_CATALOG` ile BİREBİRDİR (§71.2 KARAR 3). `Record` tipi bunu
 * derleme zamanında korur: kataloğa yeni bir olay eklendiğinde burası da eklenmek
 * ZORUNDADIR, yoksa TypeScript hata verir — zilin sessizce geride kalması imkânsız.
 */
const NOTICE_META: Record<WebhookEventName, { baslik: string; yol: string }> = {
  'insight.created': { baslik: 'Yeni bulgu', yol: '/bulgular' },
  'seo.scan_finished': { baslik: 'SEO taraması bitti', yol: '/seo' },
  'email.report_sent': { baslik: 'E-posta raporu gönderildi', yol: '/ayarlar' },
  'quota.threshold': { baslik: 'Kota eşiği aşıldı', yol: BILLING_PATH },
  'subscription.updated': { baslik: 'Abonelik güncellendi', yol: BILLING_PATH },
};

/**
 * Yükten TEK SATIRLIK özet. Yük §47.1 alan listesidir (agregat/teşhis); burada
 * yalnız o alanlar okunur ve HİÇBİRİ uydurulmaz — alan yoksa satır yazılmaz.
 */
function noticeOzet(notice: OrgNotice): string | null {
  const p = notice.payload;
  if (notice.kind === 'quota.threshold') {
    const pct = typeof p['pct'] === 'number' ? p['pct'] : null;
    return pct === null ? null : `Aylık olay limitinizin %${pct}'ine ulaştınız.`;
  }
  if (notice.kind === 'subscription.updated') {
    const durum = typeof p['status'] === 'string' ? p['status'] : null;
    return durum === null ? null : `Abonelik durumu: ${durum}`;
  }
  if (notice.kind === 'insight.created') {
    const kural = typeof p['rule'] === 'string' ? p['rule'] : null;
    return kural === null ? 'Panelde yeni bir bulgu var.' : `${kural} kuralı yeni bir bulgu üretti.`;
  }
  if (notice.kind === 'seo.scan_finished') {
    const skor = typeof p['score'] === 'number' ? p['score'] : null;
    return skor === null ? 'Tarama tamamlandı.' : `Tarama tamamlandı — skor ${skor}.`;
  }
  return null;
}

function NotificationBell() {
  const [open, setOpen] = useState(false);
  const ref = useOutsideClose(open, () => setOpen(false));
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { orgId } = useProjectContext();

  /*
   * 60 sn'lik aralık — `HealthDot` ile aynı ritim ama AYRI sorgu. WebSocket/SSE
   * AÇILMAZ (§71.4): bildirimler dakikalar ölçeğinde doğan olaylardır (insight job,
   * kota ölçümü, tarama) ve gerçek zamanlılık için kalıcı bağlantı taşımak, ürünün
   * kazandığından fazlasını maliyete yazardı.
   */
  const notices = useQuery({
    queryKey: qk.orgNotices(orgId ?? ''),
    queryFn: ({ signal }) => getOrgNotices(orgId as string, signal),
    enabled: orgId !== null,
    retry: false,
    refetchInterval: 60_000,
    staleTime: 30_000,
  });

  const okundu = useMutation({
    mutationFn: (upTo: number) => markOrgNoticesRead(orgId as string, upTo),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: qk.orgNotices(orgId ?? '') });
    },
  });

  const liste = notices.data?.notices ?? [];
  const unread = notices.data?.unread ?? 0;
  // `upTo`: kullanıcının GÖRDÜĞÜ en yeni bildirim. Zil açıkken doğan bir bildirim
  // bu değerin üstünde kalır ve "tümünü okundu say" onu KAPSAMAZ (§75.3).
  const enYeni = liste[0]?.id ?? 0;

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        data-testid="bildirim-zili"
        aria-label={unread === 0 ? 'Bildirimler' : `Bildirimler (${unread} okunmamış)`}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className={`relative flex h-8 w-8 items-center justify-center rounded-lg transition-colors ${
          open ? 'bg-[#f3f4f6]' : 'hover:bg-[#f3f4f6]'
        }`}
      >
        <Bell className="h-4 w-4 text-[#6b7280]" />
        {unread > 0 ? (
          <span
            data-testid="bildirim-rozeti"
            className="absolute -right-0.5 -top-0.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-brand px-1 text-[10px] font-bold text-white"
          >
            {unread > 99 ? '99+' : unread}
          </span>
        ) : null}
      </button>

      {open ? (
        <div
          role="menu"
          data-testid="bildirim-paneli"
          className="absolute right-0 top-10 z-50 w-80 overflow-hidden rounded-xl border border-[#e5e7eb] bg-white shadow-xl"
        >
          <div className="flex items-center justify-between border-b border-[#f3f4f6] px-3 py-2">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-[#9ca3af]">
              Bildirimler
            </p>
            {unread > 0 ? (
              <button
                type="button"
                data-testid="bildirim-tumunu-oku"
                disabled={okundu.isPending || enYeni === 0}
                onClick={() => okundu.mutate(enYeni)}
                className="text-[12px] text-brand transition-opacity hover:opacity-80 disabled:opacity-40"
              >
                Tümünü okundu say
              </button>
            ) : null}
          </div>

          <div className="max-h-80 overflow-y-auto">
            {liste.length === 0 ? (
              /*
                §75.4 — boş durum ÇIKIŞLIDIR: bildirimlerin NE ZAMAN doğduğunu söyler.
                "Bildirim yok" tek başına, kullanıcıya zilin çalışıp çalışmadığını
                sordururdu.
              */
              <p data-testid="bildirim-bos" className="px-3 py-4 text-[12px] text-[#9ca3af]">
                Henüz bildirim yok. Yeni bir bulgu üretildiğinde, SEO taraması
                bittiğinde, kota eşiği aşıldığında ve abonelik değiştiğinde burada
                görünür.
              </p>
            ) : (
              liste.map((notice) => {
                const meta = NOTICE_META[notice.kind];
                const ozet = noticeOzet(notice);
                return (
                  <button
                    key={notice.id}
                    type="button"
                    data-testid="bildirim-satiri"
                    data-kind={notice.kind}
                    data-okunmadi={notice.read_at === null ? '1' : '0'}
                    onClick={() => {
                      setOpen(false);
                      navigate(meta.yol);
                    }}
                    className={`flex w-full flex-col items-start gap-0.5 border-b border-[#f9fafb] px-3 py-2.5 text-left transition-colors hover:bg-[#f9fafb] ${
                      notice.read_at === null ? 'bg-[#fffaf6]' : ''
                    }`}
                  >
                    <span className="flex w-full items-center gap-1.5">
                      {notice.read_at === null ? (
                        <span
                          aria-hidden="true"
                          className="h-1.5 w-1.5 shrink-0 rounded-full bg-brand"
                        />
                      ) : null}
                      <span className="text-[13px] font-medium text-[#111827]">{meta.baslik}</span>
                    </span>
                    {ozet !== null ? (
                      <span className="text-[12px] text-[#6b7280]">{ozet}</span>
                    ) : null}
                    <span className="text-[11px] text-[#9ca3af]">
                      {formatDateTime(notice.created_at)}
                    </span>
                  </button>
                );
              })
            )}
          </div>

          {notices.data?.truncated === true ? (
            /* §33.5 — tavan SÖYLENİR; sessiz kesme "hepsi bu" yalanını üretirdi. */
            <p className="border-t border-[#f3f4f6] px-3 py-2 text-[11px] text-[#9ca3af]">
              Yalnız en yeni bildirimler gösteriliyor.
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Avatar menüsü (§27.7)                                                       */
/* -------------------------------------------------------------------------- */

/** Ad-soyaddan iki, yoksa e-postadan iki harf. Türkçe büyütme için 'tr' locale'i. */
export function avatarInitials(fullName: string | null, email: string | null): string {
  const source = fullName !== null && fullName.trim() !== '' ? fullName.trim() : (email ?? '');
  if (source === '') return 'DX';
  const parts = source.split(/[\s._@-]+/).filter((part) => part !== '');
  const letters = parts.length >= 2 ? `${parts[0]?.[0] ?? ''}${parts[1]?.[0] ?? ''}` : source.slice(0, 2);
  return letters.toLocaleUpperCase('tr-TR');
}

/**
 * §27.7 — "Sağ üst avatar menüsü: ad-soyad/e-posta + 'Çıkış' (logout → /giris)".
 *
 * Servis belirteciyle (§27.6) gezilirken `users` kaydı yoktur; menü o durumda kimliği
 * "Servis belirteci" olarak yazar — panelin kim olarak baktığını gizlemek, yanlışlıkla
 * owner yetkisiyle iş yapmaya yol açardı.
 */
function AvatarMenu() {
  const [open, setOpen] = useState(false);
  const ref = useOutsideClose(open, () => setOpen(false));
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user, serviceToken, memberships } = useAuth();
  const { pid } = useProjectContext();

  const signOut = useMutation({
    mutationFn: () => logout(),
    // Sunucu 401/404 dönse bile kullanıcı girişe DÖNMELİ: çerez zaten geçersizse
    // panelde kalmak yanlış olur. Bu yüzden başarı/hata aynı yere gider.
    onSettled: () => {
      queryClient.clear();
      // §42.5 — org VE proje seçimi silinir: aynı tarayıcıda giriş yapan bir sonraki
      // kullanıcı, öncekinin org'u/projesiyle açılıp ilk sayfada 403 görmemeli.
      clearActiveOrg();
      clearActivePid();
      navigate(LOGIN_PATH, { replace: true });
    },
  });

  const email = user?.email ?? null;
  const fullName = user?.fullName ?? null;
  const role = memberships.find((m) => m.pid === pid)?.role;
  const title = fullName ?? email ?? (serviceToken ? 'Servis belirteci' : 'Oturum yok');
  const subtitle = fullName !== null && email !== null ? email : serviceToken ? 'ADMIN_TOKEN (§27.6)' : null;

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        data-testid="avatar-menu"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Hesap menüsü"
        onClick={() => setOpen((v) => !v)}
        className={`flex h-8 w-8 select-none items-center justify-center rounded-lg text-[11px] font-bold text-brand transition-colors ${
          open ? 'bg-[#ffe7d5]' : 'bg-[#fff4ec] hover:bg-[#ffe7d5]'
        }`}
      >
        {avatarInitials(fullName, email)}
      </button>

      {open ? (
        <div
          role="menu"
          className="absolute right-0 top-10 z-50 w-64 overflow-hidden rounded-xl border border-[#e5e7eb] bg-white shadow-xl"
        >
          <div className="border-b border-[#f3f4f6] px-3 py-2.5">
            <p className="truncate text-[13px] font-medium text-[#111827]">{title}</p>
            {subtitle !== null ? (
              <p className="truncate text-[12px] text-[#9ca3af]">{subtitle}</p>
            ) : null}
            {role !== undefined ? (
              <p className="mt-1 text-[11px] text-[#9ca3af]">
                Bu projedeki rolünüz: {ROLE_LABELS[role]}
              </p>
            ) : serviceToken ? (
              <p className="mt-1 text-[11px] text-[#9ca3af]">Tüm projelerde sahip yetkisi</p>
            ) : null}
          </div>
          {/*
            §73.1 — hesap sayfasına giriş. Servis belirtecinde ÇİZİLMEZ: §27.6'nın
            `users` kaydı yoktur, sayfadaki üç kartın (kimlik/parola/oturumlar)
            hiçbiri onun için anlamlı değildir ve hepsi 400 dönerdi.
          */}
          {!serviceToken ? (
            <button
              type="button"
              data-testid="hesap-link"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                navigate(ACCOUNT_PATH);
              }}
              className="flex w-full items-center gap-2 border-b border-[#f3f4f6] px-3 py-2.5 text-left text-[13px] text-[#374151] transition-colors hover:bg-[#f9fafb]"
            >
              <UserCircle className="h-3.5 w-3.5 text-[#9ca3af]" aria-hidden="true" />
              Hesap
            </button>
          ) : null}
          <button
            type="button"
            data-testid="cikis"
            role="menuitem"
            disabled={signOut.isPending}
            onClick={() => signOut.mutate()}
            className="flex w-full items-center gap-2 px-3 py-2.5 text-left text-[13px] text-[#374151] transition-colors hover:bg-[#f9fafb] disabled:opacity-50"
          >
            <LogOut className="h-3.5 w-3.5 text-[#9ca3af]" aria-hidden="true" />
            {signOut.isPending ? 'Çıkılıyor…' : 'Çıkış'}
          </button>
        </div>
      ) : null}
    </div>
  );
}

function TopHeader({ onOpenPalette }: { onOpenPalette: () => void }) {
  return (
    <header className="fixed left-0 right-0 top-0 z-30 flex h-12 items-center justify-between border-b border-[#e5e7eb] bg-white px-4">
      <div className="flex items-center gap-3">
        <HeaderProjectPicker />
        <div className="flex items-center gap-2">
          {/* §27.7 giriş/davet kartlarıyla aynı işaret — tek bileşende tutulur. */}
          <BrandMark size={20} />
          <span className="font-gloock text-[17px] text-[#111827]">DXP</span>
        </div>
      </div>

      {/*
        §76.1 — kutu artık BUTONDUR, `<input>` DEĞİL.
        Buradaki alan hiçbir zaman gerçek bir metin girişi olmadı: yazılan şey hiçbir
        yere gitmiyordu. Paletin kendi girdisi vardır (odak tuzağı + klavye gezinmesi
        orada kurulur); üst çubuktaki öğe onu AÇAR. Bir input taklidi bırakmak,
        kullanıcıya iki farklı arama kutusu varmış gibi görünürdü.
      */}
      <button
        type="button"
        data-testid="arama-kutusu"
        onClick={onOpenPalette}
        aria-label="Ara (Command K)"
        className="hidden h-8 w-80 items-center gap-2 rounded-lg bg-[#f3f4f6] px-3 text-left transition-colors hover:bg-[#e9eaec] md:flex"
      >
        <Search className="h-3.5 w-3.5 shrink-0 text-[#9ca3af]" />
        <span className="flex-1 text-[12px] text-[#9ca3af]">Sayfa, oturum veya bulgu ara</span>
        <span className="shrink-0 rounded border border-[#e5e7eb] bg-white px-1 py-0.5 text-[10px] text-[#9ca3af]">
          ⌘ K
        </span>
      </button>

      <div className="flex items-center gap-3">
        <HealthDot />
        <RefreshButton />
        <NotificationBell />
        <AvatarMenu />
      </div>
    </header>
  );
}

/* -------------------------------------------------------------------------- */
/* İkon rayı                                                                   */
/* -------------------------------------------------------------------------- */

function IconRail() {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  return (
    <aside
      className={`fixed left-0 top-12 z-20 flex h-[calc(100vh-48px)] ${RAIL_W} flex-col items-center border-r border-[#e5e7eb] bg-white px-1.5 pb-3 pt-2`}
    >
      {/*
        Ray SABİT yüksekliktedir (100vh − üst çubuk) ama §13 listesi 15 girişe kadar
        uzar: 1080px'in altındaki ekranlarda son girişler (Faturalama, Webhook'lar,
        Kılavuz) kırpılıyor ve HİÇBİR ŞEKİLDE erişilemiyordu — sayfa gövdesinin
        kaydırılması rayı hareket ettirmez, çünkü ray `fixed`tir.

        `min-h-0` şart: flex sütununda `flex-1` bir öğenin kendiliğinden minimum
        boyutu içeriği kadardır; sıfırlanmazsa kutu taşar ve `overflow-y-auto`
        hiçbir zaman devreye girmez. Düğmeler `shrink-0`: alan daralınca
        sıkışıp okunmaz hâle gelmek yerine kaydırılırlar.
      */}
      <nav
        aria-label="Ana gezinme"
        className="rail-scroll flex w-full min-h-0 flex-1 flex-col gap-0.5 overflow-y-auto"
      >
        {NAV_ITEMS.map((item) => {
          const active = item.end ? pathname === item.to : pathname.startsWith(item.to);
          const IconComponent = item.icon;
          return (
            <button
              key={item.to}
              type="button"
              title={item.description}
              onClick={() => navigate(item.to)}
              className={`flex w-full shrink-0 flex-col items-center justify-center gap-1 rounded-xl py-2.5 transition-all duration-150 ${
                active
                  ? 'bg-[#f3f4f6] text-[#111827]'
                  : item.primary
                    ? 'text-brand hover:bg-[#f3f4f6]'
                    : 'text-[#6b7280] hover:bg-[#f3f4f6] hover:text-[#111827]'
              }`}
            >
              <IconComponent className="h-5 w-5 shrink-0" />
              {/*
                `text-center` + `leading-tight`: iki satıra sarılan etiketler
                ("Genel Bakış", yumuşak tireyle bölünenler) flex kutusunda sola
                yaslanıyor ve `leading-none` ile satırlar birbirine değiyordu.
              */}
              <span
                className={`text-center text-[10px] font-medium leading-tight ${
                  active ? 'text-[#111827]' : item.primary ? 'text-brand' : 'text-[#6b7280]'
                }`}
              >
                {item.railLabel ?? item.label}
              </span>
            </button>
          );
        })}
      </nav>
    </aside>
  );
}

/**
 * §44.4 — kota banner'ı.
 *
 * YAPTIRIM YUMUŞAKTIR: bu şerit hiçbir şeyi engellemez, yalnız SÖYLER. Veri akmaya
 * devam eder (§44.4 kararı — yanlış yapılandırılmış bir sayaç, ödeme yapan müşterinin
 * verisini sessizce çöpe atamamalı).
 *
 * Üç durum: limit içinde (şerit YOK), %80+ (uyarı), %100+ (aşım). Aşım 30 günü geçince
 * dil sertleşir — o noktada sorun teknik değil ticari bir konuşmadır.
 */
const AŞIM_UZUN_GUN = 30;

function KotaBanner() {
  const { orgId } = useProjectContext();
  const { data } = useQuery({
    queryKey: qk.orgUsage(orgId ?? ''),
    queryFn: ({ signal }) => getOrgUsage(orgId as string, signal),
    enabled: orgId !== null,
    retry: false,
    // Kota gecelik bir büyüklüktür; her odak değişiminde yeniden sormak gereksiz.
    staleTime: 5 * 60_000,
  });

  const usage = data?.usage;
  // §44.4 — ölçülmemiş kota UYARI DEĞİLDİR: yeni bir org'un kullanımı henüz yoktur.
  if (usage === undefined || !usage.measured || usage.pct < 80) return null;

  const asimda = usage.pct >= 100;
  const gun =
    usage.overQuotaAt === null
      ? 0
      : Math.floor((Date.now() - Date.parse(usage.overQuotaAt)) / 86_400_000);
  const uzunSure = asimda && gun >= AŞIM_UZUN_GUN;

  return (
    <div
      data-testid="kota-banner"
      data-durum={asimda ? 'asim' : 'uyari'}
      className={`fixed ${RAIL_LEFT} right-0 top-12 z-40 px-6 py-2 text-[12px] ${
        asimda
          ? 'border-b border-[#fecaca] bg-[#fef2f2] text-[#991b1b]'
          : 'border-b border-[#fde68a] bg-[#fffbeb] text-[#92400e]'
      }`}
    >
      <span className="font-medium">
        {asimda ? 'Aylık olay limitiniz aşıldı' : 'Aylık olay limitinizin %80’ini geçtiniz'}
      </span>{' '}
      <span className="num">
        {formatNumber(usage.events)} / {formatNumber(usage.limit)} ({usage.pct}%) · {usage.plan.name}
      </span>
      {' — '}
      {uzunSure
        ? `${gun} gündür limit dışındasınız; planınızı yükseltmek için bizimle iletişime geçin.`
        : 'Veri toplanmaya DEVAM EDİYOR; planınızı yükseltmeyi düşünebilirsiniz.'}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Kabuk                                                                       */
/* -------------------------------------------------------------------------- */

export interface LayoutProps {
  /** Rota kabuğu olarak kullanıldığında boş bırakılır — `<Outlet />` çizilir. */
  children?: ReactNode;
  /**
   * Verilirse ağa HİÇ çıkılmaz (testler/hikâyeler listeyi doğrudan verir).
   * Verilmezse §42.5 gereği SEÇİLİ ORG'un projeleri çekilir.
   */
  projects?: ProjectOption[];
}

export default function Layout({ children, projects: projectsProp }: LayoutProps) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  // §43.2 sihirbaz yönlendirmesi servis belirtecini MUAF tutar (aşağıdaki gerekçe).
  const { serviceToken } = useAuth();

  /*
   * §42.5 — org listesi. Oturum yoksa AuthGate zaten /giris'e atmıştır, bu yüzden
   * burada 401 beklenmez; retry KAPALI çünkü org'suz bir kullanıcı için doğru cevap
   * "boş liste"dir ve tekrar denemek onu değiştirmez.
   */
  const orgsQuery = useQuery({
    queryKey: qk.orgs(),
    queryFn: ({ signal }) => getOrgs(signal),
    retry: false,
  });
  const orgs = useMemo(() => orgsQuery.data?.orgs ?? [], [orgsQuery.data]);

  const [orgId, setOrgIdState] = useState<string | null>(() => readActiveOrg());

  /*
   * SEÇİMİ LİSTEYLE UYUMLU TUT.
   *
   * `useState` YALNIZCA mount'ta çalışır: localStorage'daki org bu kullanıcıya ait
   * olmayabilir (aynı tarayıcıda başka hesap) ya da silinmiş olabilir. O durumda
   * seçim listenin ilk org'una DÜZELTİLİR — düzeltilmezse her sayfa 403 alır ve
   * seçicide hiçbir satır "aktif" görünmez.
   */
  useEffect(() => {
    if (orgs.length === 0) return;
    if (orgId !== null && orgs.some((o) => o.id === orgId)) return;
    const ilk = orgs[0] as OrgSummary;
    setOrgIdState(ilk.id);
    writeActiveOrg(ilk.id);
  }, [orgs, orgId]);

  const setOrgId = (oid: string): void => {
    setOrgIdState(oid);
    writeActiveOrg(oid);
  };

  const orgRole = useMemo(() => orgs.find((o) => o.id === orgId)?.role ?? null, [orgs, orgId]);

  /*
   * §43.2/4 — ORG'SUZ KULLANICI SİHİRBAZA DÜŞER ve sihirbaz ATLANAMAZ.
   *
   * Yönlendirme guard'ın ALTINDA, kabuğun İÇİNDE durur: org listesi zaten burada
   * çekiliyor, ikinci bir sorgu açmak gereksiz olurdu. Üç koruma birlikte çalışır:
   *
   *  1. `isSuccess` — sorgu BAŞARIYLA "boş liste" demiş olmalı. Hata (404 org ucu
   *     olmayan sunucu, 503 Redis/PG) "org yok" DEĞİLDİR; AuthGate'in 401-dışı
   *     hatalarda yönlendirmeme kararıyla aynı fail-open ilkesi — aksi hâlde eski
   *     bir sunucuya bakan panel, herkesi çıkışsız bir sihirbaza kilitlerdi.
   *  2. Servis belirteci MUAF — §42.5 `POST /api/orgs` servis belirtecini reddeder
   *     (sahipsiz org olmaz). Onu sihirbaza atmak, tamamlayamayacağı bir ekranda
   *     bırakmak olurdu; ADMIN_TOKEN ile koşan e2e'ler de burada takılırdı.
   *  3. Zaten sihirbazdaysak yönlendirme yok (sonsuz döngü koruması).
   */
  const orgsuz = orgsQuery.isSuccess && orgs.length === 0 && !serviceToken;
  const kurulumda = pathname.toLowerCase().replace(/\/+$/, '') === ONBOARDING_PATH;

  useEffect(() => {
    if (!orgsuz || kurulumda) return;
    navigate(ONBOARDING_PATH, { replace: true });
  }, [orgsuz, kurulumda, navigate]);

  /*
   * §42.5 — proje listesi ORG'A FİLTRELİDİR. `projectsProp` verilmişse ağa HİÇ
   * çıkılmaz: DOM testleri ve Storybook benzeri kullanımlar listeyi doğrudan verir.
   */
  const projectsQuery = useQuery({
    queryKey: qk.orgProjects(orgId ?? ''),
    queryFn: ({ signal }) => getOrgProjects(orgId as string, signal),
    enabled: projectsProp === undefined && orgId !== null,
    retry: false,
  });

  const projects = useMemo<ProjectOption[]>(() => {
    if (projectsProp !== undefined) return projectsProp;
    /*
     * §72.5/1 — liste boşken SAHTE bir proje ÜRETİLMEZ.
     *
     * Eskiden `DEFAULT_PROJECTS` ('prj_demo', "Demo Mağaza") döner ve gerekçe "ilk
     * boyamada seçici boş kalmasın" olarak yazılırdı. Bu gerekçe kendi kendini
     * çürütüyordu: kullanıcının OLMAYAN bir projesi varmış gibi gösteriliyor, pid o
     * değerde takılıyor ve panelin HER sayfası 403 alıyordu — yani "boş görünmesin"
     * diye söylenen küçük yalan, ürünü tamamen kullanılamaz kılıyordu.
     */
    return (projectsQuery.data?.projects ?? []).map((p) => ({
      id: p.id,
      name: p.name,
      archived_at: p.archived_at ?? null,
      domain: p.domain,
    }));
  }, [projectsProp, projectsQuery.data]);

  const [pid, setPidState] = useState<string>(
    () => readActivePid() ?? projects[0]?.id ?? ACTIVE_PID,
  );

  /*
   * pid'i de listeyle uyumlu tut — AYNI tuzak: `useState` mount'ta koşar ve o an
   * liste henüz gelmemiştir. Düzeltilmezse pid 'prj_demo'da (ya da localStorage'daki
   * eski/başka org'a ait bir projede) TAKILI kalır ve kullanıcının o projede üyeliği
   * yoksa panelin her sayfası 403 döner.
   *
   * Düzeltme localStorage'a da YAZILIR: aksi hâlde bir sonraki yenilemede yine aynı
   * geçersiz pid okunur ve düzeltme her açılışta baştan yapılırdı.
   *
   * §72.4 — seçim AKTİF projelere önceliklidir: arşivli bir projeyi varsayılan
   * yapmak, kullanıcıyı veri akmayan bir ekranla karşılardı.
   */
  useEffect(() => {
    if (projects.length === 0) return;
    if (projects.some((p) => p.id === pid)) return;
    const ilkAktif = projects.find((p) => (p.archived_at ?? null) === null);
    const yeni = (ilkAktif ?? (projects[0] as ProjectOption)).id;
    setPidState(yeni);
    writeActivePid(yeni);
  }, [projects, pid]);

  const setPid = (next: string): void => {
    setPidState(next);
    writeActivePid(next);
  };

  const value = useMemo<ProjectContextValue>(
    () => ({ pid, setPid, projects, orgs, orgId, setOrgId, orgRole }),
    [pid, projects, orgs, orgId, orgRole],
  );

  /*
   * §76.1 — GLOBAL ⌘K / Ctrl+K kısayolu. ⌘K'yi sınayan TEK yer burasıdır.
   *
   * DÜZELTİLMİŞ YORUM (2026-08-18): burada sayfa tuş dinleyicilerinin "ayrı
   * elemanlara bağlı" olduğu yazıyordu; `Sessions.tsx` aslında DOCUMENT düzeyinde
   * bir `keydown` dinleyicisi kuruyor (modalı ESC ile kapatmak için — ok tuşları
   * değil; ok tuşları yalnız `CommandPalette`tedir, `ReplayPlayer` ise hiç global
   * dinleyici kurmaz). Çakışma yok, çünkü kabuk yalnız ⌘K'ye bakıyor — ama yeni
   * bir global kısayol ekleyecek kişi, kabuğun tek global dinleyici OLMADIĞINI
   * bilmeli. Bir metin alanında yazarken de çalışır — ⌘K
   * tarayıcıda metin girişi üretmeyen bir kombinasyondur.
   */
  const [paletOpen, setPaletOpen] = useState(false);
  useEffect(() => {
    function onKey(event: KeyboardEvent): void {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setPaletOpen((v) => !v);
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  return (
    <ProjectContext.Provider value={value}>
      <TopHeader onOpenPalette={() => setPaletOpen(true)} />
      <CommandPalette open={paletOpen} onClose={() => setPaletOpen(false)} />
      <IconRail />
      <KotaBanner />
      <main className={`min-h-screen bg-[#fafafa] ${RAIL_PAD} pt-12`}>{children ?? <Outlet />}</main>
    </ProjectContext.Provider>
  );
}
